import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Stack,
    Chip,
    Fade
} from "@mui/material";
import * as React from 'react';
import projects from "../../data/projects";

const ProjectCard = ({title, technologies, image, index, link, description}) => (
    <Fade in={true} timeout={500 + (index * 100)}>
        <Card
            component="a"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                '&:hover .project-image': {
                    transform: 'scale(1.05)',
                },
                '&:hover .project-description-overlay': {
                    opacity: 1,
                },
                '&:hover': {
                    '& .tech-stack': {
                        opacity: 1,
                        transform: 'translateY(0)',
                    }
                }
            }}
        >
            <Box sx={{position: 'relative', paddingTop: '56.25%', overflow: 'hidden'}}>
                <CardMedia
                    component="img"
                    image={image}
                    alt={title}
                    className="project-image"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 100%)',
                    }}
                />
                <Box
                    className="project-description-overlay"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        opacity: 0,
                        transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        p: 2,
                    }}
                >
                    <Typography variant="body2">
                        {description}
                    </Typography>
                </Box>
            </Box>

            <CardContent sx={{flexGrow: 1, p: 3}}>
                <Typography variant="h5" component="h2" gutterBottom fontWeight={700}>
                    {title}
                </Typography>

                <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    gap={1}
                    className="tech-stack"
                    sx={{
                        mt: 2,
                        opacity: {xs: 1, md: 0.7},
                        transform: {xs: 'translateY(0)', md: 'translateY(5px)'},
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    {technologies.map((tech, index) => (
                        <Chip
                            key={index}
                            icon={
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/icons/${tech.icon}.svg`}
                                    alt={tech.name}
                                    sx={{width: 16, height: 16}}
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            }
                            label={tech.name}
                            size="small"
                            sx={{
                                fontWeight: 500,
                                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                                '&:hover': {
                                    backgroundColor: 'rgba(37, 99, 235, 0.15)',
                                }
                            }}
                        />
                    ))}
                </Stack>
            </CardContent>
        </Card>
    </Fade>
);

function Projects() {
    return (
        <Container maxWidth="xl" sx={{py: {xs: 4, md: 8}}}>
            <Box sx={{textAlign: 'center', mb: {xs: 4, md: 6}}}>
                <Typography
                    variant="h2"
                    component="h1"
                    gutterBottom
                    sx={{
                        fontWeight: 800,
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}
                >
                    Projects
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{maxWidth: 600, mx: 'auto'}}>
                    A collection of my work and side projects
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr)',
                    },
                    gap: {xs: 2, md: 3},
                }}
            >
                {projects && projects.length > 0 ? (
                    projects.map((project, index) => (
                        <ProjectCard
                            key={index}
                            title={project.title}
                            technologies={project.technologies}
                            image={project.imageSrc}
                            description={project.description}
                            index={index}
                            link={project.link}
                        />
                    ))
                ) : (
                    <Box sx={{gridColumn: '1 / -1', textAlign: 'center', py: 8}}>
                        <Typography variant="h6" color="text.secondary">
                            No projects found
                        </Typography>
                    </Box>
                )}
            </Box>
        </Container>
    );
}

export default Projects;